import type { VoidFunctionComponent } from 'react';

import Badge from '../../../generic/Badge/Badge';
import Button from '../../../generic/Button';
import { icons } from '../../../generic/Icon';
import FixBadge from '../../../issues/IssuesInbox/IssueItem/FixBadge';
import type { UpgradeDistance } from '../../../../../shared/vulns/types';
import type { BadgeColor } from '../../../generic/Badge/Badge';

import styles from './RemediationPanels.module.less';

const remediationTypes = ['current', 'complete', 'partial', 'no-fix', 'unable-to-calculate'] as const;
type RemediationType = typeof remediationTypes[number];

const REMEDIATION_NAME: Record<RemediationType, string> = {
  current: 'CURRENT VERSION',
  complete: 'COMPLETE FIX',
  partial: 'PARTIAL FIX',
  'no-fix': 'NO FIX',
  'unable-to-calculate': 'UNABLE TO CALCULATE',
};

const REMEDIATION_TEXT: Record<RemediationType, string> = {
  current: 'You are exposed to this vulnerability with this version.',
  complete: 'Nearest update to fix all vulnerabilities found on this dependency.',
  partial: 'Nearest update to fix this vulnerability. This may not remediate all vulnerabilities.',
  'no-fix': 'FOSSA cannot find a fix for this vulnerability at this time.',
  'unable-to-calculate': 'FOSSA cannot calculate a fix for this vulnerability.',
};

const REMEDIATION_STYLE: Record<RemediationType, string> = {
  current: styles.remediationSuggestionCurrentVersion,
  complete: styles.remediationSuggestionCompleteFix,
  partial: styles.remediationSuggestionPartialFix,
  'no-fix': styles.remediationSuggestionNoFix,
  // share the same style as 'no-fix' because they are both unfixable
  'unable-to-calculate': styles.remediationSuggestionNoFix,
};

const REMEDIATION_BADGE_COLOR: Record<RemediationType, BadgeColor> = {
  current: 'light-red',
  complete: 'dark-green',
  partial: 'light-green',
  'no-fix': 'light-red',
  // share the same color as 'no-fix' because they are both unfixable
  'unable-to-calculate': 'light-red',
};

//  Shown instead of REMEDIATION_TEXT['unable-to-calculate'] when the dependency's
//  vulnerability was surfaced through an SBOM-supplied CPE, where updating the
//  dependency is the actionable fix.
const UNABLE_TO_CALCULATE_CPE_TEXT = 'FOSSA couldn\'t calculate accurate results because a CPE was used. Update this dependency for better results.';

type Props = {
  type: RemediationType;
  version?: string;
  upgradeDistance?: UpgradeDistance;
  useFixBadge?: boolean;
  /**
   * Whether this vulnerability was surfaced via an SBOM-supplied CPE.
   * Only honoured for the 'unable-to-calculate' type.
   */
  isDetectedViaCpe?: boolean;
  /**
   * Opens the manual dependency overwrite flow. When omitted, no action button is rendered.
   * Only honoured for the 'unable-to-calculate' type.
   */
  onUpdateDependency?: () => void;
};

//  Helper which returns the heading text, depending on the type.
const getHeading = ({ type, version }: Props): `Current version ${string}` | 'No fix available' | `Upgrade to ${string}` | 'Unable to calculate' => {
  switch (type) {
    case 'current':
      return `Current version ${version}`;
    case 'no-fix':
      return 'No fix available';
    case 'unable-to-calculate':
      return 'Unable to calculate';
    default:
      return `Upgrade to ${version}`;
  }
};

/**
 * PackageVersionVulnerabilityDrawerRemediationSuggestion shows a panel of
 * specific information detailing one of three "fixability" states for a vuln:
 * complete fix, partial fix, or no fix.
 */
const PackageVersionVulnerabilityDrawerRemediationSuggestion: VoidFunctionComponent<Props> = ({
  type,
  version,
  upgradeDistance,
  useFixBadge, // Whether to show the fix badge.  Useful for up until partial fix is enabled
  isDetectedViaCpe,
  onUpdateDependency,
}) => {
  const heading = getHeading({ type, version });
  const hasFix = !['no-fix', 'unable-to-calculate'].includes(type);
  const isUnableToCalculate = type === 'unable-to-calculate';
  const shouldShowCpeTreatment = isUnableToCalculate && !!isDetectedViaCpe;
  const shouldShowUpdateButton = isUnableToCalculate && !!onUpdateDependency;
  const bodyText = shouldShowCpeTreatment ? UNABLE_TO_CALCULATE_CPE_TEXT : REMEDIATION_TEXT[type];

  return (
    <div className={`${styles.remediationSuggestion} ${REMEDIATION_STYLE[type]}`}>
      <div className={styles.remediationSuggestionHeading}>
        <h5>{heading}</h5>
        {hasFix && version && useFixBadge && (
          <FixBadge version={version} distance={upgradeDistance} />
        )}
        {hasFix && <Badge color={REMEDIATION_BADGE_COLOR[type]}>{REMEDIATION_NAME[type]}</Badge>}
        {shouldShowCpeTreatment && (
          <Badge color="alert-warning" icon={icons.triangleExclamation}>Detected via CPE</Badge>
        )}
      </div>
      <p className={styles.remediationSuggestionParagraph}>{bodyText}</p>
      {shouldShowUpdateButton && (
        <div className={styles.remediationSuggestionActions}>
          <Button
            type="secondary"
            size="small"
            icon={icons.add}
            testId="remediation-update-dependency"
            onClick={onUpdateDependency}
          >
            Update Dependency
          </Button>
        </div>
      )}
    </div>
  );
};

export default PackageVersionVulnerabilityDrawerRemediationSuggestion;
